import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Avatar, Typography, Badge, Tooltip, Divider, alpha
} from '@mui/material';
import DashboardIcon  from '@mui/icons-material/DashboardRounded';
import TaskIcon       from '@mui/icons-material/AssignmentRounded';
import PeopleIcon     from '@mui/icons-material/PeopleRounded';
import BusinessIcon   from '@mui/icons-material/BusinessRounded';
import ChecklistIcon  from '@mui/icons-material/FactCheckRounded';
import NotifIcon      from '@mui/icons-material/NotificationsRounded';
import PersonIcon     from '@mui/icons-material/PersonRounded';
import LogoutIcon     from '@mui/icons-material/LogoutRounded';
import MenuIcon       from '@mui/icons-material/MenuRounded';
import MenuOpenIcon   from '@mui/icons-material/MenuOpenRounded';
import { logout }     from '../../app/slices/authSlice';
import { getInitials }from '../../utils/helpers';

const W_OPEN  = 248;
const W_CLOSE = 68;

export default function AppLayout() {
  const [open, setOpen] = useState(true);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { pathname } = useLocation();
  const { user }  = useSelector(s => s.auth);
  const { unreadCount } = useSelector(s => s.notifications);

  const nav = [
    { label: 'Dashboard',    icon: <DashboardIcon />, path: '/dashboard',    roles: ['Admin','Manager','User'] },
    { label: 'All Tasks',    icon: <TaskIcon />,      path: '/tasks',        roles: ['Admin'] },
    { label: 'My Tasks',     icon: <ChecklistIcon />, path: '/my-tasks',     roles: ['Manager','User'] },
    { label: 'Users',        icon: <PeopleIcon />,    path: '/users',        roles: ['Admin'] },
    { label: 'Departments',  icon: <BusinessIcon />,  path: '/departments',  roles: ['Admin'] },
    { label: 'Approvals',    icon: <ChecklistIcon />, path: '/approvals',    roles: ['Admin'] },
    {
      label: 'Notifications',
      icon: <Badge badgeContent={unreadCount || null} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
              <NotifIcon />
            </Badge>,
      path: '/notifications',
      roles: ['Admin','Manager','User'],
    },
    { label: 'Profile', icon: <PersonIcon />, path: '/profile', roles: ['Admin','Manager','User'] },
  ].filter(item => item.roles.includes(user?.role));

  const handleLogout = () => { dispatch(logout()); navigate('/login'); };

  return (
    <Box display="flex" height="100vh" overflow="hidden" sx={{ background: '#f1f5f9' }}>
      {/* Sidebar */}
      <Drawer variant="permanent" sx={{
        width: open ? W_OPEN : W_CLOSE, flexShrink: 0,
        transition: 'width 0.22s cubic-bezier(.4,0,.2,1)',
        '& .MuiDrawer-paper': {
          width: open ? W_OPEN : W_CLOSE,
          overflowX: 'hidden',
          transition: 'width 0.22s cubic-bezier(.4,0,.2,1)',
          boxSizing: 'border-box',
        },
      }}>
        {/* Logo */}
        <Box display="flex" alignItems="center" justifyContent={open ? 'space-between' : 'center'}
          px={open ? 2 : 0} py={2} minHeight={60}>
          {open && (
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{
                width: 30, height: 30, borderRadius: 2,
                background: 'linear-gradient(135deg,#4f46e5,#818cf8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.85rem', fontFamily: "'Sora', sans-serif" }}>A</Typography>
              </Box>
              <Typography variant="h6" fontWeight={800} fontFamily="'Sora', sans-serif"
                sx={{ color: '#0f172a', letterSpacing: '-0.5px' }}>
                Align
              </Typography>
            </Box>
          )}
          <IconButton onClick={() => setOpen(p => !p)} size="small"
            sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
            {open ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Box>

        <Divider />

        {/* Nav items */}
        <List sx={{ flex: 1, py: 1.5, px: 1 }}>
          {nav.map(item => {
            const active = pathname === item.path || pathname.startsWith(item.path + '/');
            return (
              <Tooltip key={item.path} title={open ? '' : item.label} placement="right">
                <ListItemButton onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2, mb: 0.5, minHeight: 44,
                    justifyContent: open ? 'flex-start' : 'center',
                    px: open ? 1.5 : 1.5,
                    bgcolor: active ? alpha('#4f46e5', 0.08) : 'transparent',
                    color: active ? '#4f46e5' : '#64748b',
                    '&:hover': { bgcolor: active ? alpha('#4f46e5', 0.12) : '#f8fafc', color: '#0f172a' },
                    transition: 'all 0.15s',
                  }}>
                  <ListItemIcon sx={{
                    minWidth: open ? 36 : 'unset', color: 'inherit',
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: active ? 600 : 400 }}
                    />
                  )}
                  {active && open && (
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#4f46e5' }} />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>

        <Divider />

        {/* User footer */}
        <Box px={open ? 1.5 : 1} py={1.5} display="flex"
          alignItems="center" justifyContent={open ? 'space-between' : 'center'}>
          {open ? (
            <>
              <Box display="flex" alignItems="center" gap={1.5} overflow="hidden">
                <Avatar sx={{
                  width: 34, height: 34, fontSize: '0.75rem', fontWeight: 700,
                  background: 'linear-gradient(135deg,#4f46e5,#818cf8)',
                }}>
                  {getInitials(user?.name)}
                </Avatar>
                <Box overflow="hidden">
                  <Typography variant="body2" fontWeight={600} noWrap color="text.primary">{user?.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{user?.role}</Typography>
                </Box>
              </Box>
              <Tooltip title="Sign out">
                <IconButton size="small" onClick={handleLogout} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Sign out" placement="right">
              <IconButton size="small" onClick={handleLogout} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Drawer>

      {/* Main content */}
      <Box component="main" flex={1} overflow="auto" p={3}>
        <Outlet />
      </Box>
    </Box>
  );
}
